import { supabase } from "@/integrations/supabase/client";
import { validateEmail } from "./validateEmail";
import { extractFunctionErrorMessage } from "./functionsError";
import { getAnalyticsIds } from "./analytics";
import { pushDataLayer } from "./dataLayer";

export interface LeadPayload {
  form_name: string;
  name?: string;
  company?: string;
  email?: string;
  phone?: string;
  request_type?: string;
  message?: string;
  product_context?: string;
  category_context?: string;
  referral_partner?: string;
  extra?: Record<string, unknown>;
}

export interface LeadResult {
  ok: boolean;
  error?: string;
  request_id?: string;
}

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Submit a lead. Returns `{ ok, error?, request_id? }`.
 * Validates email/phone client-side and normalises the edge-function response.
 */
export async function submitLead(payload: LeadPayload): Promise<LeadResult> {
  // Light client-side validation — catches obvious garbage before the round-trip.
  if (!payload?.form_name) return { ok: false, error: "Missing form name" };
  if (payload.email) {
    const check = validateEmail(payload.email);
    if (!check.valid) return { ok: false, error: check.message };
  }
  if (payload.phone && payload.phone.replace(/\D/g, "").length < 7) {
    return { ok: false, error: "Please enter a valid phone number." };
  }
  if (payload.message && payload.message.length > 5000) {
    return { ok: false, error: "Message is too long (5000 char max)." };
  }

  try {
    const { data, error } = await supabase.functions.invoke("submit-lead", {
      body: {
        ...payload,
        page_path: typeof window !== "undefined" ? window.location.pathname : "",
        page_url: typeof window !== "undefined" ? window.location.href : "",
        page_name: typeof document !== "undefined" ? document.title : "",
        referrer: typeof document !== "undefined" ? document.referrer : "",
        // Links the lead to the visitor's journey; the server records the conversion.
        analytics: getAnalyticsIds(),
      },
    });

    if (error) {
      console.error("[submitLead] invoke error", error);
      const detail = await extractFunctionErrorMessage(error);
      return { ok: false, error: detail || "Network error. Please try again." };
    }
    if (!data || data.ok === false) {
      console.error("[submitLead] server returned not-ok", data);
      return { ok: false, error: data?.error || "Submission failed. Please try again.", request_id: data?.request_id };
    }
    pushDataLayer("generate_lead", { form_name: payload.form_name, request_type: payload.request_type || null });
    return { ok: true, request_id: data.request_id };
  } catch (e) {
    console.error("[submitLead] unexpected exception", e);
    return { ok: false, error: "Unexpected error. Please try again or call us." };
  }
}
